import { CreditReportTypes } from 'enums/credit-report-types';
import { useMutation, useQuery } from 'react-query';
import {
  getCreditRiskCategories,
  getInsights,
  getSnapshotLatest,
  postSnapshotGeneration,
  getTimelineActivities,
  getCompanySnapshot,
  getConversations,
  getMonitorInsights,
} from 'services/monitor';
import { postExportClientList } from './export';
import { patchUnarchiveClient } from './archive';
import {
  patchBulkAddManagerClients,
  patchBulkArchiveClients,
  postBulkSyncClients,
  postBulkUpdateTagsClients,
} from './bulkActions';
import { getFacilities, getCounterFacilities } from './facilities';
import { deleteTag, patchEditTag, postCreateTag } from './tags';
import { ActivityType } from 'enums/activity-type';
import { getCompanyMonitorCheck } from './getCompanyMonitorCheck';

export const useQueryGetCreditRiskCategories = () =>
  useQuery(
    'credit-risk-categories',
    async () => {
      const result = await getCreditRiskCategories();
      return result?.data ?? [];
    },
    {
      retry: false,
      staleTime: 1000 * 60 * 60, // 1 hour
    },
  );

export const useQueryGetInsights = (entityId, updatedSince, managerId) =>
  useQuery(
    ['insights', updatedSince, managerId],
    async () => {
      const result = await getInsights(entityId, updatedSince, managerId);
      return result?.data ?? [];
    },
    {
      retry: false,
      staleTime: 1000 * 60 * 60, // 1 hour,
      enabled: !!entityId,
    },
  );

export const useQueryGetFacilitiesCount = (entityId, queryParams) =>
  useQuery(
    ['count-facilities', queryParams],
    async () => {
      const result = await getCounterFacilities(entityId, queryParams);
      return result?.data ?? [];
    },
    {
      retry: false,
      staleTime: 1000 * 60 * 60, // 1 hour,
      enabled: !!entityId,
    },
  );

export const useQueryGetActivityTimeline = (options) =>
  useQuery(
    ['activity-timeline', options.limit],
    async () => {
      const result = await getTimelineActivities(options);
      return result?.data.filter((activity) => activity.type !== 3);
    },
    {
      keepPreviousData: true,
    },
  );

export const useQueryGetActivityFeedbackTimeline = (options) =>
  useQuery(
    ['activity-timeline-feedback', options.limit],
    async () => {
      const result = await getTimelineActivities(options);
      return result?.data.filter((activity) => activity.type === ActivityType.feedback);
    },
    {
      keepPreviousData: true,
    },
  );

export const useQueryGetMonitorInsights = () =>
  useQuery(['monitor-insights'], async () => {
    const result = await getMonitorInsights();
    return result?.data;
  });

export const useQueryGetSnapshotLatestExperianData = (entityId) =>
  useQuery(
    ['snapshot-latest', 'experian', entityId],
    async () => {
      const result = await getSnapshotLatest(entityId);
      const experianData =
        result?.data?.creditReports?.find(
          (report) => report.provider === CreditReportTypes.Experian,
        ) ?? null;
      if (experianData) {
        experianData.generatedAt = new Date(result.data.generatedAt);
        experianData.companyInfo = result.data.companyInfo;
        experianData.integrations = result?.data?.integrations;
      }
      return { result, experianData };
    },
    {
      enabled: !!entityId,
      retry: false,
    },
  );

export const useMutationPostSnapshotGeneration = (entityId) =>
  useMutation(async (entityId) => {
    const result = await postSnapshotGeneration(entityId);
    const data = result?.data ?? {};
    return data;
  });

export const useQueryGetFacilities = (entityId, enabled = false) =>
  useQuery(
    'facilities',
    async () => {
      const result = await getFacilities(entityId);
      return result?.data ?? [];
    },
    {
      retry: false,
      staleTime: 1000 * 60 * 60, // 1 hour
      enabled,
    },
  );

export const useQueryGetConversations = (companyId, excludeInsights, updatedSince, managerList) =>
  useQuery(
    ['conversations', { updatedSince, managerList }],
    async () => {
      const result = await getConversations(companyId, excludeInsights, updatedSince, managerList);
      return result?.data ?? [];
    },
    {
      retry: false,
      staleTime: 1000 * 60 * 60,
      enabled: !!companyId,
      keepPreviousData: true,
    },
  );

export const useMutationPatchBulkArchiveClients = () =>
  useMutation(async (entityIds) => {
    await patchBulkArchiveClients(entityIds);
  });

export const useMutationPostBulkSyncClients = () =>
  useMutation(async (entityIds) => {
    await postBulkSyncClients(entityIds);
  });

export const useMutationPostBulkUpdateTagsClients = () =>
  useMutation(async (entityList) => {
    await postBulkUpdateTagsClients(entityList);
  });

export const useMutationPostCreateTag = () =>
  useMutation(async (tagName) => {
    return await postCreateTag(tagName);
  });

export const useMutationPatchBulkAddManagerClients = () =>
  useMutation(async (entityBody) => {
    return await patchBulkAddManagerClients(entityBody);
  });

export const useQueryGetCompanySnapshot = (entityId) =>
  useQuery(
    ['get-company-snapshot', entityId],
    async () => {
      const result = await getCompanySnapshot(entityId);
      return result?.data ?? [];
    },
    {
      enabled: !!entityId,
    },
  );

export const useMutationPostExportClientList = () =>
  useMutation('export-clients', async ({ companyId, payload }) => {
    const result = await postExportClientList(companyId, payload);
    const data = result?.data ?? {};
    return data;
  });

export const useMutationPatchUnarchiveClient = () =>
  useMutation(async (entityIds) => {
    await patchUnarchiveClient(entityIds);
  });

export const useMutationDeleteTag = () =>
  useMutation(async (assignmentId) => {
    return await deleteTag(assignmentId);
  });

export const useMutationPatchEditTag = () =>
  useMutation(async ({ assignmentId, tagName }) => {
    return await patchEditTag(assignmentId, tagName);
  });

export const useMutationGetCompanyMonitorCheck = () =>
  useMutation(async ({ entityId, companyNumber }) => {
    const result = await getCompanyMonitorCheck(entityId, companyNumber);
    return result?.data ?? {};
  });
